import { Paginated } from "@common/interfaces/base";
import { BatchActivityType } from "@common/interfaces/batchActivity";
import { BuildingMetadata, IBuildingInfo, IBuildingInfos, IBuildingUnits, SelectGatewayLoraDTO } from "@common/interfaces/gisfo";
import { IID_ISSUE_LINK } from "@common/interfaces/interfaceData";
import { EspletatoOkGISFOOptions } from "@common/interfaces/interfaceSettings";
import { IIssue } from "@common/interfaces/issue";
import { SysMessageTopic } from "@common/interfaces/sysMessage";
import { IUser } from "@common/interfaces/user";
import { EntityResponse } from "./ack";

export type RealtimeScopeKind = 'global' | 'project' | 'issue' | 'linked_preview';
export type RealtimeScopeSyncMode = 'full' | 'partial';
export type RealtimeScopeInvalidationReason = 'workflow_changed' | 'visibility_changed' | 'access_revoked';
export type RealtimeClientCommandName = 'enterHome' | 'enterProject' | 'leaveProject' | 'enterIssue' | 'leaveIssue' | 'enterLinkedPreview' | 'leaveLinkedPreview' | 'entityCommand' | 'featureCommand';
export type RealtimeDataCommandName = 'create' | 'update' | 'delete' | (string & {});

export type RealtimeCursor = {
  entity: string;
  id: string;
  lastUpdated: string;
};

export interface RealtimeClientCommandPayloads {
  enterHome: {
    cursors?: RealtimeCursor[];
  };
  enterProject: {
    projectId: string;
    cursors?: RealtimeCursor[];
  };
  leaveProject: {
    projectId: string;
  };
  enterIssue: {
    issueId: string;
    cursors?: RealtimeCursor[];
  };
  leaveIssue: {
    issueId: string;
  };
  enterLinkedPreview: {
    previewKey?: string;
    linkedIssueId: string;
    linkedProjectId?: string;
    parentProjectId?: string;
    taskTypeId?: string;
    interfaceType?: string;
    linkInterfaceName?: string;
    entities: RealtimeLinkedPreviewEntity[];
    cursors?: RealtimeCursor[];
  };
  leaveLinkedPreview: {
    previewKey?: string;
    linkedIssueId?: string;
  };
  entityCommand: RealtimeEntityCommandPayload;
  featureCommand: RealtimeFeatureCommandPayload;
}

export type RealtimeLinkedPreviewEntity = {
  interface: string;
  interfaceName?: string;
  entityName?: string;
  instance?: string;
};

export type RealtimeClientCommand<TCommand extends RealtimeClientCommandName = RealtimeClientCommandName> = {
  id: string;
  type: TCommand;
  expectsCompletion?: boolean;
  payload: RealtimeClientCommandPayloads[TCommand];
};

export interface RealtimeScopeSyncStartedPayload {
  scopeKey: string;
  scopeKind: RealtimeScopeKind;
  syncMode: RealtimeScopeSyncMode;
  entities: string[];
}

export interface RealtimeScopeReadyPayload {
  scopeKey: string;
  scopeKind: RealtimeScopeKind;
  syncMode: RealtimeScopeSyncMode;
  entities: string[];
}

export interface RealtimeScopeInvalidatedPayload {
  scopeKey: string;
  reason: RealtimeScopeInvalidationReason;
}

export interface RealtimeEntitySetChangedPayload {
  scopeKey: string;
  removed: string[];
}

export interface RealtimeSessionInvalidPayload {
  reason: string;
  details: Record<string, any> | null;
}

/**
 * Motivi di invalidazione sessione concordati fra backend e client. `COMMAND_RATE_LIMIT` indica che
 * il client ha superato il rate-limit di comandi (flood): il client deve fare dump dello stato,
 * svuotare la cache e tornare al login.
 */
export const REALTIME_SESSION_INVALID_REASONS = {
  COMMAND_RATE_LIMIT: 'command_rate_limit_exceeded',
} as const;

export interface RealtimeCommandAcceptedPayload {
  requestId: string;
  command: RealtimeClientCommandName;
  expectsCompletion?: boolean;
  body?: any;
}

export interface RealtimeCommandRejectedPayload {
  requestId?: string;
  command?: RealtimeClientCommandName;
  code: string;
  message: string;
  details?: Record<string, any>;
}

export interface RealtimeCommandCompletedPayload {
  requestId: string;
  command: RealtimeClientCommandName;
  ok: boolean;
  body?: any;
  error?: {
    code: string;
    message: string;
    details?: Record<string, any>;
  };
}

export interface RealtimeCommandQueuePayload {
  requestId: string;
  command: RealtimeClientCommandName;
  position: number;
  queued: number;
  active: number;
  concurrency: number;
}

export type RealtimeUpdateOrigin = {
  socketId: string;
  requestId?: string;
  userId?: string;
  mutationSeq?: number;
};

export type RealtimeScopedEntityResponse<TEntity = any[]> = EntityResponse<TEntity> & {
  scopeKey: string;
  origin?: RealtimeUpdateOrigin;
};

export type RealtimeEntityUpdatesPayload<TEntity = any[]> = RealtimeScopedEntityResponse<TEntity>;
export type RealtimeEntitySyncPayload<TEntity = any[]> = Paginated<RealtimeScopedEntityResponse<TEntity>>;
export type RealtimeBatchActivityAction =
  | 'checkpoint'
  | 'remove_issue'
  | 'remove_project'
  | 'remove_appointment'
  | 'remove_appointments_by_user'
  | 'appointment_resync'
  | 'appointment_resync_batch'
  | 'issue_visible'
  | 'appointment_visible';
export type RealtimeBatchActivityRemovalReason =
  | 'removed'
  | 'moved'
  | 'not_interesting';
export type RealtimeBatchActivityMessage = {
  id: string;
  updatedAt: string;
  type: BatchActivityType;
  action: RealtimeBatchActivityAction;
  reason?: RealtimeBatchActivityRemovalReason;
  payload:
    | Record<string, never>
    | { issueId: string }
    | { projectId: string }
    | { appointmentId: string }
    | { userIds: string[] }
    | { entities: EntityResponse<any[]>[] }
    | {
        issueId: string;
        projectId: string;
        entities: EntityResponse<any[]>[];
      }
    | {
        appointmentId: string;
        entities: EntityResponse<any[]>[];
      };
};
export type RealtimeBatchActivitySyncPayload = Paginated<{
  scopeKey: string;
  entityName: string;
  activities: RealtimeBatchActivityMessage[];
}>;
export type RealtimeSysMessageType =
  | 'sync'
  | 'news_project'
  | 'client_connected'
  | 'client_disconnected'
  | 'client_info'
  | 'maintenance'
  | 'message'
  | (string & {});
export type RealtimeSysMessagePayload<TPayload = any> = {
  type: RealtimeSysMessageType;
  payload?: TPayload;
  legacyTopic?: SysMessageTopic;
  id?: string;
  requestId?: string;
  sourceClientId?: string;
};
export type RealtimeCurrentUserPayload = Omit<IUser, 'password'>;

export interface RealtimeMainSessionChangedPayload {
  // Elezione della sessione principale (main tab) per uno specifico client/browser (clientId).
  // Solo la main session si occupa del refresh del token, evitando refresh concorrenti fra tab.
  isMain: boolean;
  clientId: string;
  socketId: string;
}

export interface RealtimeServerToClientEvents {
  entity_updates: RealtimeEntityUpdatesPayload<any[]>;
  sys_message: RealtimeSysMessagePayload;
  current_user_updated: RealtimeCurrentUserPayload;
  main_session_changed: RealtimeMainSessionChangedPayload;
  scope_sync_started: RealtimeScopeSyncStartedPayload;
  scope_ready: RealtimeScopeReadyPayload;
  scope_invalidated: RealtimeScopeInvalidatedPayload;
  entity_set_changed: RealtimeEntitySetChangedPayload;
  session_invalid: RealtimeSessionInvalidPayload;
  command_accepted: RealtimeCommandAcceptedPayload;
  command_queued: RealtimeCommandQueuePayload;
  command_queue_updated: RealtimeCommandQueuePayload;
  command_rejected: RealtimeCommandRejectedPayload;
  command_completed: RealtimeCommandCompletedPayload;
}

export type RealtimeServerToClientEventName = keyof RealtimeServerToClientEvents;

export type RealtimeServerToClientMessageEnvelope<TEvent extends RealtimeServerToClientEventName> = {
  type: TEvent;
  payload: RealtimeServerToClientEvents[TEvent];
};

export type RealtimeServerToClientMessage = {
  [K in RealtimeServerToClientEventName]: RealtimeServerToClientMessageEnvelope<K>
}[RealtimeServerToClientEventName];

export interface RealtimeAckEventPayloads {
  entity_sync: RealtimeEntitySyncPayload<any[]>;
  batch_activity_sync: RealtimeBatchActivitySyncPayload;
  sys_message: RealtimeSysMessagePayload;
}

export type RealtimeAckEventName = keyof RealtimeAckEventPayloads;

export type RealtimeAckEnvelope<TEvent extends RealtimeAckEventName> = {
  id: string;
  payload: {
    type: TEvent;
    payload: RealtimeAckEventPayloads[TEvent];
  };
};

export type RealtimeAckMessage = {
  [K in RealtimeAckEventName]: RealtimeAckEnvelope<K>
}[RealtimeAckEventName];

/** Limiti di troncamento dei campi testuali dell'errore di ack: valgono sia in produzione lato
 * client (prima dell'invio) sia lato server (difesa sul confine, il payload arriva dal browser). */
export const REALTIME_ACK_ERROR_MESSAGE_MAX_CHARS = 500;
export const REALTIME_ACK_ERROR_STACK_MAX_CHARS = 4000;

/**
 * Dettaglio dell'errore che il client allega quando rifiuta un `ws_req` (`ok: false`).
 * Viene riportato nella telemetria server-side per capire cosa è fallito lato browser.
 */
export interface RealtimeAckError {
  /** Messaggio d'errore, già normalizzato a stringa dal client. */
  message: string;
  /** Nome/classe dell'errore quando disponibile (es. `TypeError`). */
  name?: string;
  /** Stack del frontend, troncato. */
  stack?: string;
  /** Contesto applicativo aggiuntivo (es. tipo di richiesta in fallimento). */
  context?: Record<string, any>;
}

/** Risposta del client a un `ws_req`: esito e, in caso di rifiuto, il motivo. */
export interface RealtimeAckResponse {
  id: string;
  ok: boolean;
  /** Valorizzato solo con `ok: false`. */
  error?: RealtimeAckError;
}

function truncateAckText(value: string, maxChars: number): string {
  return value.length > maxChars ? `${value.slice(0, maxChars)}…` : value;
}

/**
 * Normalizza un errore arbitrario (Error, stringa, oggetto) nel dettaglio da allegare all'ack.
 * Ritorna `undefined` se non c'è nulla di utile da riportare.
 */
export function toRealtimeAckError(error: unknown, context?: Record<string, any>): RealtimeAckError | undefined {
  if (error === undefined || error === null) {
    return context ? { message: 'Unknown client error', context } : undefined;
  }

  if (error instanceof Error) {
    return sanitizeRealtimeAckError({
      message: error.message || error.name || 'Unknown client error',
      name: error.name,
      stack: error.stack,
      context,
    });
  }

  if (typeof error === 'string') {
    return sanitizeRealtimeAckError({ message: error, context });
  }

  const candidate = error as Partial<RealtimeAckError>;
  return sanitizeRealtimeAckError({
    message: typeof candidate?.message === 'string' ? candidate.message : safeStringifyAckError(error),
    ...(typeof candidate?.name === 'string' && { name: candidate.name }),
    ...(typeof candidate?.stack === 'string' && { stack: candidate.stack }),
    context,
  });
}

/**
 * Ripulisce un dettaglio d'errore ricevuto dal client: scarta forme non valide e tronca i campi
 * testuali, così che uno stack anomalo non finisca integralmente nei log/telemetria.
 */
export function sanitizeRealtimeAckError(error: unknown): RealtimeAckError | undefined {
  if (!error || typeof error !== 'object') {
    return undefined;
  }

  const candidate = error as Partial<RealtimeAckError>;
  const message = typeof candidate.message === 'string' && candidate.message.length
    ? candidate.message
    : 'Unknown client error';

  return {
    message: truncateAckText(message, REALTIME_ACK_ERROR_MESSAGE_MAX_CHARS),
    ...(typeof candidate.name === 'string' && { name: truncateAckText(candidate.name, REALTIME_ACK_ERROR_MESSAGE_MAX_CHARS) }),
    ...(typeof candidate.stack === 'string' && { stack: truncateAckText(candidate.stack, REALTIME_ACK_ERROR_STACK_MAX_CHARS) }),
    ...(candidate.context && typeof candidate.context === 'object' && { context: candidate.context }),
  };
}

function safeStringifyAckError(error: unknown): string {
  try {
    return JSON.stringify(error) ?? String(error);
  } catch {
    return String(error);
  }
}

export interface RealtimeServerToClientTransportEvents {
  ws_msg: RealtimeServerToClientMessage;
  ws_req: RealtimeAckMessage;
}

export interface RealtimeClientToServerTransportEvents {
  ws_cmd: RealtimeClientCommand;
  ws_req_ack: RealtimeAckResponse;
}

export interface RealtimeEntityCommandPayloads {}
export interface RealtimeFeatureCommandPayloads {
  "feature:issue-link": {
    "create-link": Partial<IID_ISSUE_LINK>;
    "create-issue-and-link": {
      issue: Partial<IIssue>;
      parentIssueId: string;
      typeInterfaceFrom: string;
    };
    "update-validity": {
      issueId: string;
      issueTypeInterfaceName: string;
    };
    "delete-link": { id: string };
  };
  "feature:comment": {
    "trash": {
      commentId: string;
      withMentions?: boolean;
    };
    "trash-linked-project": {
      commentId: string;
      projectId: string;
    };
    "copy-to-linked-project": {
      commentId: string;
      typeInterfaceName: string;
      projectId: string;
    };
    "copy-to-linked-by-project": {
      commentId: string;
      typeInterfaceName: string;
      projectId: string;
    };
    "get-all-by-copy-of": {
      commentId: string;
    };
  };
  "feature:attachment": {
    "trash": {
      attachmentId: string;
    };
    "trash-linked-project": {
      attachmentId: string;
      projectId: string;
    };
    "after-creation": {
      attachmentId: string;
      typeInterfaceName?: string;
    };
    "copy-to-linked-project": {
      attachmentId: string;
      typeInterfaceName: string;
      projectId: string;
    };
    "copy-to-linked-by-project": {
      attachmentId: string;
      typeInterfaceName: string;
      projectId: string;
    };
    "get-all-by-copy-of": {
      attachmentId: string;
    };
  };
  "feature:espletato-ok-checklist-gisfo": {
    "create-in-gisfo": {
      id: string;
      tipoCl: number;
    };
    "save-in-gisfo": {
      id: string;
      settings: EspletatoOkGISFOOptions;
    };
    "close-in-gisfo": {
      id: string;
    };
    "send-photos-to-gisfo": {
      id: string;
    };
  };
  "feature:assurance-ticket": {
    "create-task-on-guasti-nid": {
      sourceIssueId: string;
      projectDestinationId: string;
      issueTypeDestinationId: string;
      linkInterfaceName?: string;
    };
  };
  "feature:checklist-gisfo": {
    "refresh": {
      id: string;
      fkEntity?: string;
    };
  };
  "feature:fault-management": {
    "send-to-gisfo": {
      id: string;
    };
    "retry-send-to-gisfo": {
      id: string;
    };
  };
  "feature:ol-activation-request": {
    "search-buildings": IBuildingInfos;
  };
  "feature:ol-appuntamento": {
    "retry": {
      id: string;
    };
  };
  "feature:ol-communication": {
    "retry": {
      id: string;
    };
  };
  "feature:risorsa-lora": {
    "get-lora-from-gisfo": SelectGatewayLoraDTO;
    "refresh": {
      id?: string;
      fkEntity: string;
    };
    "clear": {
      id: string;
    };
    "create-fault": {
      id: string;
      activityType: number;
    };
  };
  "feature:issue": {
    "manual-sub": string;
    "remove-trashed": {
      ids: string[];
    };
    "change-task-type": {
      id: string;
      destType: string;
    };
    "change-sla": {
      id: string;
      slaId: string;
    };
  };
  "feature:tag": {
    "tags-by-project": {
      projectId: string;
    };
  };
  "feature:signal": {
    "building:refresh": {
      id: string;
    };
    "building:select": {
      id: string;
      building: IBuildingInfo | BuildingMetadata;
    };
    "building:updateUiCapacity": {
      id: string;
      buildingPk: string;
      uiResidenziali: number;
      uiBusiness: number;
      addUnits?: boolean;
    };
    "ui:refresh": {
      id: string;
      updateEntity?: boolean;
      listeners?: boolean;
    };
    "ui:update": {
      id: string;
      payload: IBuildingUnits;
      updateEntity: boolean;
    };
    "ui:create": {
      id: string;
      payload: IBuildingUnits;
    };
    "ui:delete": {
      id: string;
      pkBuildingUnits: string;
    };
    "ui:associate": {
      issueId: string;
      pkBuildingUnit: number;
    };
    "ui:contract:associate": {
      issueId: string;
      pkBuildingUnit: number;
      contractId: number;
    };
    "risorsa:reservation": {
      issueId: string;
    };
    "risorsa:complete-reservation": {
      issueId: string;
    };
    "risorsa:release-signal": {
      issueId: string;
    };
    "risorsa:refresh": {
      issueId: string;
    };
  };
}

export type RealtimeEntityCommandPayload = {
  entityName: string;
  command: RealtimeDataCommandName;
  dto?: any;
  id?: string;
  projectId?: string;
  mutationSeq?: number;
};

export type RealtimeFeatureCommandPayload = {
  target: string;
  command: string;
  dto?: any;
  projectId?: string;
};

type RealtimeKnownEntityCommand = {
  [TEntity in keyof RealtimeEntityCommandPayloads & string]: {
    [TCommand in keyof RealtimeEntityCommandPayloads[TEntity] & string]: {
      entityName: TEntity;
      command: TCommand;
      dto: RealtimeEntityCommandPayloads[TEntity][TCommand];
      id?: string;
      projectId?: string;
    }
  }[keyof RealtimeEntityCommandPayloads[TEntity] & string]
}[keyof RealtimeEntityCommandPayloads & string];

type RealtimeKnownFeatureCommand = {
  [TTarget in keyof RealtimeFeatureCommandPayloads & string]: {
    [TCommand in keyof RealtimeFeatureCommandPayloads[TTarget] & string]: {
      target: TTarget;
      command: TCommand;
      dto: RealtimeFeatureCommandPayloads[TTarget][TCommand];
      projectId?: string;
    }
  }[keyof RealtimeFeatureCommandPayloads[TTarget] & string]
}[keyof RealtimeFeatureCommandPayloads & string];

export type RealtimeTypedEntityCommandPayload =
  [keyof RealtimeEntityCommandPayloads] extends [never]
    ? never
    : RealtimeKnownEntityCommand;

export type RealtimeTypedFeatureCommandPayload =
  [keyof RealtimeFeatureCommandPayloads] extends [never]
    ? never
    : RealtimeKnownFeatureCommand;
